import {
    useCallback,
    useMemo, useState,
} from 'react'
import merge from 'lodash/merge'
import {
    GridGetRowsError,
    GridGetRowsParams,
    GridUpdateRowError,
} from '@mui/x-data-grid-pro'

import {
    useJWTToken,
} from 'app/Auth'
import simpleApi from 'app/services/simpleApi'
import {
    RequestConfig,
} from 'app/types/request.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    useArchivedTableControl,
} from 'app/shared-components/TableControls/ShowArchived'
import {
    useSkyNetPagination,
} from 'app/shared-components/SkyNetPagination'
import queryClient, {
    invalidateQueries,
} from 'app/queryClient'
import {
    SkyNetDataSourceProvider,
} from '../SkyNetDataGrid.types'
import {
    getIncludeFiltersFromFilterModel, getOtherFilterForBodyFromFilterModel,
} from '../utils/FilterModel.utils'

/* DataSource with fetchQuery */

type RequestParams = {
    sortField?: string,
    sortType?: 'desc' | 'asc',
    searchAll?: string,
    paginationModeMatches?: boolean,
    rows?: number,
    start?: number,
    archived?: boolean
    includeFilters: Record<string, any>,
}

type DataSourceParams = {
    request: RequestConfig,
    key: RequestKeys,
    rowsPerPage?: number,
}

const useSkyNetDataGridDataSource = ({
    request,
    key,
    rowsPerPage = 25,
}: DataSourceParams): SkyNetDataSourceProvider => {
    const paginationOptions = useSkyNetPagination(rowsPerPage)
    const {
        setMatches,
    } = paginationOptions
    const showArchived = useArchivedTableControl(key)
    const token = useJWTToken()
    const [
        error,
        setError,
    ] = useState<string>(null)
    const [
        isFetching,
        setIsFetching,
    ] = useState<boolean>(false)

    const handleError = useCallback((rowsError: GridGetRowsError | GridUpdateRowError) => {
        setError(rowsError.message)
    }, [])

    const customDataSource = useMemo(() => {
        return {
            getRows: async (params: GridGetRowsParams) => {
                const includeFilters = getIncludeFiltersFromFilterModel(params.filterModel)
                const otherFilterForBody = getOtherFilterForBodyFromFilterModel(params.filterModel)
                const payload: RequestParams = {
                    sortField: params.sortModel[0]?.field,
                    sortType: params.sortModel[0]?.sort,
                    searchAll: params.filterModel?.quickFilterValues?.join(' ') || undefined,
                    paginationModeMatches: rowsPerPage > 0,
                    rows: params.paginationModel.pageSize || rowsPerPage,
                    start: params.paginationModel.pageSize * params.paginationModel.page,
                    archived: showArchived,
                    includeFilters,
                    ...otherFilterForBody,
                }

                const queryKey = [
                    key,
                    request.url,
                    payload,
                ]

                let result = {
                    rows: [],
                    rowCount: 0,
                }

                try {
                    setIsFetching(true)
                    const response = await queryClient.fetchQuery({
                        queryKey,
                        queryFn: () => {
                            return simpleApi({
                                ...request,
                                data: merge(payload, request.data),
                                token,
                            })
                        },
                        staleTime: 10000, // important to prevent irrelevant calls,
                    })

                    if (response.matches) {
                        setMatches(response.matches)
                    }

                    result = {
                        rows: response.items,
                        rowCount: response.matches || response.items.length,
                    }
                } catch (e) {
                    handleError({
                        message: e,
                    } as GridGetRowsError)
                } finally {
                    setIsFetching(false)
                }
                return result
            },
        }
    }, [
        rowsPerPage,
        showArchived,
        key,
        request,
        token,
        setMatches,
        handleError,
    ])

    const invalidate = useCallback(() => {
        invalidateQueries([key])
    }, [key])

    return useMemo(() => {
        return {
            dataSource: customDataSource,
            onDataSourceError: handleError,
            errorMessage: error,
            isFetching,
            paginationOptions,
            showArchived,
            key,
            invalidate,
            request,
        }
    }, [
        customDataSource,
        handleError,
        error,
        isFetching,
        paginationOptions,
        showArchived,
        key,
        invalidate,
        request,
    ])
}

export default useSkyNetDataGridDataSource
